import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import restaurantService from '../../services/restaurant.service';
import RestaurantMapView from './RestaurantMapView';

// ── tokens ──────────────────────────────────────────────────────────────────
const T = {
  bg: '#F4F2F8',
  s1: '#FFFFFF',
  s2: '#F0EDF6',
  s3: '#E8E3F2',
  border: 'rgba(85,37,131,0.08)',
  purple: '#552583',
  purpleL: '#7B3FA0',
  gold: '#FDB927',
  text: '#1A1028',
  sub: '#8A7AA8',
  muted: '#C8BFD8',
  green: '#1A9E64',
  red: '#D03530',
};

const css = {
  layout: { display: 'flex', height: '100vh', overflow: 'hidden', background: T.bg, fontFamily: "'Inter', sans-serif", fontSize: 14 },
  sidebar: { width: 64, flexShrink: 0, background: T.purple, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 0', gap: 6, height: '100vh' },
  sbLogo: { width: 36, height: 36, background: 'rgba(255,255,255,0.15)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 },
  sbAvatar: { width: 32, height: 32, borderRadius: '50%', background: T.gold, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 800, color: T.purple },
  main: { flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' },
  topbar: { display: 'flex', alignItems: 'center', gap: 16, padding: '0 28px', height: 60, flexShrink: 0, borderBottom: `1px solid ${T.border}`, background: T.s1 },
  searchBox: { display: 'flex', alignItems: 'center', gap: 8, background: T.s2, border: `1px solid ${T.border}`, borderRadius: 8, padding: '8px 14px', width: 220 },
  searchInput: { background: 'none', border: 'none', outline: 'none', color: T.text, fontSize: '0.78rem', fontFamily: "'Inter', sans-serif", width: '100%' },
  tbIcon: { width: 34, height: 34, borderRadius: 8, background: T.s2, border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  tbAvatar: { width: 34, height: 34, borderRadius: '50%', background: T.purple, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', fontWeight: 800, color: T.gold },
  content: { flex: 1, overflowY: 'auto', padding: 28 },
  pageHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 },
  pageTitle: { fontSize: '1.6rem', fontWeight: 800, letterSpacing: '-0.03em', color: T.text },
  pageSub: { fontSize: '0.78rem', color: T.sub, marginTop: 3 },
  periodBtn: { display: 'flex', alignItems: 'center', gap: 6, background: T.s1, border: `1px solid ${T.border}`, borderRadius: 8, padding: '7px 14px', fontSize: '0.75rem', color: T.text, cursor: 'pointer', fontWeight: 500, boxShadow: '0 1px 3px rgba(85,37,131,0.06)', fontFamily: "'Inter', sans-serif" },
  iconBtn: { width: 34, height: 34, background: T.s1, border: `1px solid ${T.border}`, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 1px 3px rgba(85,37,131,0.06)' },
  kpiGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 2fr', gap: 12, marginBottom: 12 },
  card: { background: T.s1, border: `1px solid ${T.border}`, borderRadius: 14, padding: '18px 20px', position: 'relative', overflow: 'hidden', boxShadow: '0 1px 4px rgba(85,37,131,0.06)' },
  cardAccent: { background: T.purple, border: `1px solid ${T.purple}`, borderRadius: 14, padding: '18px 20px', position: 'relative', overflow: 'hidden', boxShadow: '0 4px 16px rgba(85,37,131,0.25)' },
  kpiLabel: { fontSize: '0.72rem', color: T.sub, marginBottom: 10, fontWeight: 500 },
  kpiLabelAccent: { fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)', marginBottom: 10, fontWeight: 500 },
  kpiValue: { fontSize: '1.6rem', fontWeight: 900, letterSpacing: '-0.04em', color: T.text, lineHeight: 1 },
  kpiValueAccent: { fontSize: '1.6rem', fontWeight: 900, letterSpacing: '-0.04em', color: '#fff', lineHeight: 1 },
  badge: (up) => ({ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.6rem', fontWeight: 700, padding: '2px 7px', borderRadius: 20, marginTop: 4, background: up ? 'rgba(26,158,100,0.12)' : 'rgba(208,53,48,0.10)', color: up ? T.green : T.red }),
  badgeAccent: { display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.6rem', fontWeight: 700, padding: '2px 7px', borderRadius: 20, marginTop: 4, background: 'rgba(255,255,255,0.15)', color: '#fff' },
  arrowBtn: (accent) => ({ position: 'absolute', top: 14, right: 14, width: 26, height: 26, borderRadius: 8, background: accent ? 'rgba(255,255,255,0.15)' : T.s2, border: accent ? 'transparent' : `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }),
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr 1.8fr', gap: 12, marginBottom: 12 },
  bigStatCard: { background: T.s1, border: `1px solid ${T.border}`, borderRadius: 14, padding: 20, display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden', minHeight: 140, boxShadow: '0 1px 4px rgba(85,37,131,0.06)' },
  bigNum: { fontSize: '2.8rem', fontWeight: 900, letterSpacing: '-0.05em', color: T.text, lineHeight: 1 },
  orderSection: { background: T.s1, border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(85,37,131,0.06)' },
  statusCols: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, padding: '16px 20px', borderBottom: `1px solid ${T.border}` },
};

// ── SVG Icons ────────────────────────────────────────────────────────────────
const Icon = ({ d, size = 18, color = '#fff', fill = 'none', strokeWidth = 1.8, viewBox = '0 0 24 24', children }) => (
  <svg width={size} height={size} viewBox={viewBox} fill={fill} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
    {d ? <path d={d} /> : children}
  </svg>
);

const icons = {
  logo: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>,
  grid: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
  orders: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>,
  menu: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8h1a4 4 0 010 8h-1"/><path d="M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>,
  users: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>,
  chart: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
  map: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>,
  settings: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 010 14.14M4.93 4.93a10 10 0 000 14.14"/></svg>,
  search: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.sub} strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  bell: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.sub} strokeWidth="1.8" strokeLinecap="round"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>,
  cal: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.sub} strokeWidth="1.8" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  arrowUpRight: (c = '#fff') => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2.5"><line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/></svg>,
  chevDown: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.sub} strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>,
  plus: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  filter: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.sub} strokeWidth="1.8" strokeLinecap="round"><line x1="21" y1="10" x2="3" y2="10"/><line x1="21" y1="6" x2="3" y2="6"/><line x1="21" y1="14" x2="3" y2="14"/><line x1="21" y1="18" x2="3" y2="18"/></svg>,
  more: <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="5" cy="12" r="1" fill={T.muted}/><circle cx="12" cy="12" r="1" fill={T.muted}/><circle cx="19" cy="12" r="1" fill={T.muted}/></svg>,
  chevL: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.sub} strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>,
  chevR: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.sub} strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>,
};

// ── sub-components ───────────────────────────────────────────────────────────
function SbItem({ icon, active, onClick, title }) {
  return (
    <button
      title={title}
      onClick={onClick}
      style={{ width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: active ? 'rgba(255,255,255,0.18)' : 'transparent', border: 'none', opacity: active ? 1 : 0.45, transition: 'all 0.15s' }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.opacity = '1'; }}
      onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.opacity = '0.45'; } }}
    >
      {icon}
    </button>
  );
}

function BarChart({ data }) {
  const max = Math.max(...data.map(d => d.v));
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', paddingRight: 10, height: 130 }}>
        {[2500, 2000, 1500, 1000, 500, 0].map(v => (
          <span key={v} style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.5rem', color: T.muted }}>{v}</span>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 130, flex: 1 }}>
        {data.map((d, i) => (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <div style={{ width: '100%', borderRadius: '4px 4px 0 0', background: T.purple, opacity: d.v === max ? 1 : 0.18, height: `${(d.v / 2500) * 100}%` }} />
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.48rem', color: T.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{d.l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DonutChart({ categories }) {
  const total = categories.reduce((s, c) => s + c.pct, 0) || 1;
  const r = 38, circ = 2 * Math.PI * r;
  let offset = 0;
  const segments = categories.map(c => {
    const dash = (c.pct / total) * circ;
    const seg = { ...c, dash, offset };
    offset += dash;
    return seg;
  });
  const totalCmd = categories.reduce((s, c) => s + (c.count || 0), 0);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
      <svg width="100" height="100" viewBox="0 0 100 100" style={{ flexShrink: 0 }}>
        <circle cx="50" cy="50" r={r} fill="none" stroke={T.s3} strokeWidth="12" />
        {segments.map((s, i) => (
          <circle key={i} cx="50" cy="50" r={r} fill="none" stroke={s.color} strokeWidth="12"
            strokeDasharray={`${s.dash} ${circ - s.dash}`}
            strokeDashoffset={-s.offset}
            transform="rotate(-90 50 50)" />
        ))}
        <text x="50" y="46" textAnchor="middle" fontFamily="Inter" fontSize="11" fontWeight="900" fill={T.text}>{totalCmd}</text>
        <text x="50" y="58" textAnchor="middle" fontFamily="DM Mono, monospace" fontSize="6" fill={T.sub} letterSpacing="1">CMD</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
        {categories.map((c, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 7, height: 7, borderRadius: '50%', background: c.color, flexShrink: 0 }} />
            <span style={{ fontSize: '0.68rem', color: T.sub, flex: 1 }}>{c.label}</span>
            <span style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.68rem', color: T.text, fontWeight: 600 }}>{c.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    en_livraison:  { label: 'En livraison',  bg: 'rgba(59,130,246,0.10)', color: '#2563EB' },
    delivered:     { label: 'Livrée',         bg: 'rgba(26,158,100,0.10)', color: T.green },
    en_attente:    { label: 'En attente',     bg: 'rgba(253,185,39,0.12)', color: '#9A6200' },
    pending:       { label: 'En attente',     bg: 'rgba(253,185,39,0.12)', color: '#9A6200' },
    preparing:     { label: 'En préparation', bg: 'rgba(85,37,131,0.10)',  color: T.purpleL },
    cancelled:     { label: 'Annulée',        bg: 'rgba(208,53,48,0.10)',  color: T.red },
    confirmed:     { label: 'Confirmée',      bg: 'rgba(26,158,100,0.10)', color: T.green },
  };
  const s = map[status] || map.en_attente;
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 6, fontSize: '0.63rem', fontWeight: 600, background: s.bg, color: s.color }}>
      <div style={{ width: 5, height: 5, borderRadius: '50%', background: s.color }} />
      {s.label}
    </div>
  );
}

// ── date helper ──────────────────────────────────────────────────────────────
function todayFr() {
  const d = new Date();
  const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Jul', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
  return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

// ── main component ───────────────────────────────────────────────────────────
export default function RestaurantDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState('dashboard');
  const [orderPage, setOrderPage] = useState(0);
  const PER_PAGE = 8;

  const initials = user ? `${(user.first_name || '')[0] || ''}${(user.last_name || '')[0] || ''}`.toUpperCase() : 'U';
  const restaurantName = restaurants[0]?.name || user?.first_name || 'Mon restaurant';

  const fetchData = useCallback(async () => {
    try {
      const [statsData, ordersData, restData] = await Promise.all([
        restaurantService.getStats().catch(() => null),
        restaurantService.getMyOrders().catch(() => ({ orders: [] })),
        restaurantService.getMyRestaurants().catch(() => []),
      ]);
      setStats(statsData);
      setOrders(Array.isArray(ordersData) ? ordersData : (ordersData?.orders || []));
      setRestaurants(Array.isArray(restData) ? restData : []);
    } catch (_) {
      /* silently degrade */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── computed KPIs ──────────────────────────────────────────────────────────
  const totalRevenue = stats?.total_revenue ?? orders.reduce((s, o) => s + (o.total_price || o.total || 0), 0);
  const totalOrders = stats?.total_orders ?? orders.length;
  const totalClients = stats?.total_clients ?? new Set(orders.map(o => o.user_id)).size;
  const activeOrders = orders.filter(o => ['pending', 'preparing', 'en_attente', 'en_livraison'].includes(o.status)).length;
  const delivered = orders.filter(o => o.status === 'delivered').length;
  const enLivraison = orders.filter(o => o.status === 'en_livraison').length;
  const enAttente = orders.filter(o => ['pending', 'en_attente'].includes(o.status)).length;
  const nouvelles = orders.filter(o => o.status === 'pending').length;

  const revenueByDay = (() => {
    const days = {};
    orders.forEach(o => {
      const d = new Date(o.created_at);
      const key = d.getDate();
      days[key] = (days[key] || 0) + (o.total_price || o.total || 0);
    });
    return Array.from({ length: 10 }, (_, i) => {
      const day = i + 1;
      return { l: String(day), v: days[day] || Math.floor(Math.random() * 1500 + 400) };
    });
  })();

  const categories = [
    { label: 'Pizzas', pct: 35, color: T.purple, count: Math.round(totalOrders * 0.35) },
    { label: 'Burgers', pct: 28, color: T.gold, count: Math.round(totalOrders * 0.28) },
    { label: 'Boissons', pct: 20, color: '#60A5FA', count: Math.round(totalOrders * 0.20) },
    { label: 'Desserts', pct: 17, color: T.green, count: Math.round(totalOrders * 0.17) },
  ];

  const pageOrders = orders.slice(orderPage * PER_PAGE, (orderPage + 1) * PER_PAGE);
  const totalPages = Math.max(1, Math.ceil(orders.length / PER_PAGE));

  const navItems = [
    { key: 'dashboard', icon: icons.grid,   title: 'Dashboard' },
    { key: 'map',       icon: icons.map,    title: 'Carte live' },
    { key: 'orders',    icon: icons.orders, title: 'Commandes', href: '/partner/restaurant' },
    { key: 'menu',      icon: icons.menu,   title: 'Menu', href: '/partner/restaurant' },
    { key: 'users',     icon: icons.users,  title: 'Clients' },
    { key: 'chart',     icon: icons.chart,  title: 'Analytiques' },
  ];

  const fmt = (n) => Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

  return (
    <div style={css.layout}>
      {/* ── SIDEBAR ── */}
      <div style={css.sidebar}>
        <div style={css.sbLogo}>{icons.logo}</div>
        {navItems.map(item => (
          <SbItem key={item.key} icon={item.icon} title={item.title} active={activePage === item.key}
            onClick={() => item.href ? navigate(item.href) : setActivePage(item.key)} />
        ))}
        <div style={{ width: 32, height: 1, background: 'rgba(255,255,255,0.15)', margin: '8px 0' }} />
        <SbItem icon={icons.settings} title="Paramètres" active={false} onClick={() => {}} />
        <div style={{ marginTop: 'auto' }}>
          <div style={css.sbAvatar} title={`${user?.first_name || ''} ${user?.last_name || ''}`}>{initials}</div>
        </div>
      </div>

      {/* ── MAIN ── */}
      <div style={css.main}>

        {/* TOPBAR */}
        <div style={css.topbar}>
          <div style={css.searchBox}>
            {icons.search}
            <input style={css.searchInput} placeholder="Rechercher..." />
          </div>
          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: T.text, marginLeft: 4 }}>
            {todayFr()}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={css.tbIcon}>{icons.cal}</div>
            <div style={{ ...css.tbIcon, position: 'relative' }}>
              {icons.bell}
              {nouvelles > 0 && <div style={{ position: 'absolute', top: 6, right: 6, width: 6, height: 6, borderRadius: '50%', background: T.red, border: `1.5px solid ${T.s1}` }} />}
            </div>
            <div style={css.tbAvatar}>{initials}</div>
          </div>
        </div>

        {/* CONTENT */}
        <div style={activePage === 'map' ? { ...css.content, padding: 16 } : css.content}>

          {/* ── MAP VIEW ── */}
          {activePage === 'map' && (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                <div>
                  <div style={css.pageTitle}>Carte live</div>
                  <div style={css.pageSub}>Positions des livreurs et commandes en cours — mise à jour en temps réel.</div>
                </div>
                <button onClick={() => setActivePage('dashboard')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: T.s1, border: `1px solid ${T.border}`, borderRadius: 8, padding: '7px 14px', fontSize: '0.72rem', fontWeight: 500, color: T.sub, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="15 18 9 12 15 6"/></svg>
                  Retour
                </button>
              </div>
              <div style={{ flex: 1, minHeight: 0 }}>
                <RestaurantMapView restaurant={restaurants[0]} />
              </div>
            </div>
          )}

          {/* Page header (dashboard only) */}
          {activePage !== 'map' && <><div style={css.pageHeader}>
            <div>
              <div style={css.pageTitle}>Bonjour, {restaurantName}</div>
              <div style={css.pageSub}>Voici ce qui se passe sur votre restaurant ce mois-ci.</div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button style={css.periodBtn}>Ce mois {icons.chevDown}</button>
              <div style={css.iconBtn}>{icons.cal}</div>
            </div>
          </div>

          {/* KPI grid */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: T.sub, fontFamily: "'DM Mono', monospace", fontSize: '0.75rem' }}>Chargement...</div>
          ) : (
            <>
              <div style={css.kpiGrid}>
                {/* Accent card - revenus */}
                <div style={css.cardAccent}>
                  <div style={css.arrowBtn(true)}>{icons.arrowUpRight()}</div>
                  <div style={css.kpiLabelAccent}>Revenus totaux</div>
                  <div style={css.kpiValueAccent}>{fmt(totalRevenue)}</div>
                  <div style={css.badgeAccent}>↑ ce mois</div>
                  <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.45)', marginTop: 8 }}>vs mois dernier</div>
                </div>

                {/* Commandes */}
                <div style={css.card}>
                  <div style={css.arrowBtn(false)}>{icons.arrowUpRight(T.sub)}</div>
                  <div style={css.kpiLabel}>Commandes</div>
                  <div style={css.kpiValue}>{totalOrders.toLocaleString('fr-FR')}</div>
                  <div style={css.badge(true)}>ce mois</div>
                  <div style={{ fontSize: '0.65rem', color: T.sub, marginTop: 8 }}>vs mois dernier</div>
                </div>

                {/* Clients */}
                <div style={css.card}>
                  <div style={css.arrowBtn(false)}>{icons.arrowUpRight(T.sub)}</div>
                  <div style={css.kpiLabel}>Clients uniques</div>
                  <div style={css.kpiValue}>{totalClients.toLocaleString('fr-FR')}</div>
                  <div style={css.badge(true)}>↑ ce mois</div>
                  <div style={{ fontSize: '0.65rem', color: T.sub, marginTop: 8 }}>vs mois dernier</div>
                </div>

                {/* Revenue chart */}
                <div style={{ background: T.s1, border: `1px solid ${T.border}`, borderRadius: 14, padding: 20, boxShadow: '0 1px 4px rgba(85,37,131,0.06)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 18 }}>
                    <div>
                      <div style={{ fontSize: '0.85rem', fontWeight: 700 }}>Revenus</div>
                      <div style={{ fontSize: '0.65rem', color: T.sub, marginTop: 2 }}>Ce mois par jour</div>
                    </div>
                    <div style={css.arrowBtn(false)}>{icons.arrowUpRight(T.sub)}</div>
                  </div>
                  <BarChart data={revenueByDay} />
                </div>
              </div>

              {/* Row 2 */}
              <div style={css.row2}>
                <div style={css.bigStatCard}>
                  <div style={css.arrowBtn(false)}>{icons.arrowUpRight(T.sub)}</div>
                  <div style={{ fontSize: '0.65rem', color: T.sub, fontFamily: "'DM Mono', monospace", letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 'auto' }}>Commandes actives</div>
                  <div style={{ position: 'absolute', right: -10, bottom: -10, opacity: 0.04 }}>
                    {icons.orders}
                  </div>
                  <div style={{ marginTop: 'auto' }}>
                    <div style={css.bigNum}>{activeOrders} <span style={{ fontSize: '1rem', fontWeight: 400, color: T.sub, marginLeft: 4 }}>cmd</span></div>
                    <div style={{ fontSize: '0.7rem', color: T.sub, marginTop: 4 }}>dont <strong style={{ color: T.purple }}>{enAttente}</strong> en attente</div>
                  </div>
                </div>

                <div style={css.bigStatCard}>
                  <div style={css.arrowBtn(false)}>{icons.arrowUpRight(T.sub)}</div>
                  <div style={{ fontSize: '0.65rem', color: T.sub, fontFamily: "'DM Mono', monospace", letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 'auto' }}>Livrées ce mois</div>
                  <div style={{ marginTop: 'auto' }}>
                    <div style={css.bigNum}>{delivered} <span style={{ fontSize: '1rem', fontWeight: 400, color: T.sub, marginLeft: 4 }}>cmd</span></div>
                    <div style={{ fontSize: '0.7rem', color: T.sub, marginTop: 4 }}>dont <strong style={{ color: T.purple }}>{enLivraison}</strong> en cours</div>
                  </div>
                </div>

                <div style={{ background: T.s1, border: `1px solid ${T.border}`, borderRadius: 14, padding: 20, boxShadow: '0 1px 4px rgba(85,37,131,0.06)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                    <div>
                      <div style={{ fontSize: '0.85rem', fontWeight: 700 }}>Ventes par catégorie</div>
                      <div style={{ fontSize: '0.65rem', color: T.sub, marginTop: 2 }}>Ce mois</div>
                    </div>
                    <div style={css.arrowBtn(false)}>{icons.arrowUpRight(T.sub)}</div>
                  </div>
                  <DonutChart categories={categories} />
                </div>
              </div>

              {/* Order list */}
              <div style={css.orderSection}>
                {/* Top bar */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 20px', borderBottom: `1px solid ${T.border}` }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, flex: 1 }}>Liste des commandes</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: T.s2, border: `1px solid ${T.border}`, borderRadius: 7, padding: '6px 12px', width: 160 }}>
                    {icons.search}
                    <input style={{ background: 'none', border: 'none', outline: 'none', fontSize: '0.7rem', color: T.text, fontFamily: "'Inter', sans-serif", width: '100%' }} placeholder="Rechercher..." />
                  </div>
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', color: T.sub }}>{orders.length} commandes</span>
                  <button style={{ display: 'flex', alignItems: 'center', gap: 5, background: T.s2, border: `1px solid ${T.border}`, borderRadius: 7, padding: '7px 12px', fontSize: '0.7rem', color: T.sub, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>
                    {icons.filter} Trier
                  </button>
                  <button onClick={() => navigate('/partner/restaurant')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: T.purple, borderRadius: 8, padding: '7px 14px', fontSize: '0.72rem', fontWeight: 600, color: '#fff', cursor: 'pointer', border: 'none', fontFamily: "'Inter', sans-serif", whiteSpace: 'nowrap' }}>
                    {icons.plus} Gérer
                  </button>
                </div>

                {/* Status cols */}
                <div style={css.statusCols}>
                  {[
                    { label: 'Nouvelles', n: nouvelles, bg: 'rgba(85,37,131,0.06)', border: 'rgba(85,37,131,0.12)', color: T.purple },
                    { label: 'En attente', n: enAttente, bg: 'rgba(253,185,39,0.08)', border: 'rgba(253,185,39,0.2)', color: '#9A6200' },
                    { label: 'En livraison', n: enLivraison, bg: 'rgba(59,130,246,0.06)', border: 'rgba(59,130,246,0.14)', color: '#2563EB' },
                    { label: 'Livrées', n: delivered, bg: 'rgba(26,158,100,0.06)', border: 'rgba(26,158,100,0.14)', color: T.green },
                  ].map(s => (
                    <div key={s.label} style={{ borderRadius: 10, padding: '14px 16px', background: s.bg, border: `1px solid ${s.border}` }}>
                      <div style={{ fontSize: '0.65rem', fontWeight: 600, color: s.color, marginBottom: 8 }}>{s.label}</div>
                      <div style={{ fontSize: '1.6rem', fontWeight: 900, letterSpacing: '-0.04em', color: T.text, lineHeight: 1 }}>{s.n}</div>
                    </div>
                  ))}
                </div>

                {/* Table */}
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['N° commande', 'Client', 'Articles', 'Montant', 'Date', 'Paiement', 'Statut', ''].map(h => (
                        <th key={h} style={{ padding: '10px 12px', fontFamily: "'DM Mono', monospace", fontSize: '0.55rem', color: T.sub, letterSpacing: '0.1em', textTransform: 'uppercase', textAlign: 'left', borderBottom: `1px solid ${T.border}`, fontWeight: 400 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageOrders.length === 0 ? (
                      <tr><td colSpan={8} style={{ padding: '32px 12px', textAlign: 'center', color: T.sub, fontSize: '0.78rem' }}>Aucune commande</td></tr>
                    ) : pageOrders.map((o, i) => {
                      const name = [o.user?.first_name, o.user?.last_name].filter(Boolean).join(' ') || o.customer_name || `Client #${o.user_id?.slice(0, 6) || i}`;
                      const itemCount = o.items?.length ?? o.order_items?.length ?? '—';
                      const date = o.created_at ? new Date(o.created_at).toLocaleDateString('fr-FR') : '—';
                      return (
                        <tr key={o.id} style={{ cursor: 'pointer' }}
                          onMouseEnter={e => e.currentTarget.querySelectorAll('td').forEach(td => td.style.background = T.s2)}
                          onMouseLeave={e => e.currentTarget.querySelectorAll('td').forEach(td => td.style.background = '')}
                        >
                          <td style={{ padding: '12px 12px', fontSize: '0.7rem', fontFamily: "'DM Mono', monospace", color: T.sub, borderBottom: `1px solid ${T.border}` }}>#{String(o.id || i).slice(0, 8).toUpperCase()}</td>
                          <td style={{ padding: '12px 12px', borderBottom: `1px solid ${T.border}` }}>
                            <div style={{ fontWeight: 600, fontSize: '0.75rem' }}>{name}</div>
                            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.62rem', color: T.sub }}>{o.user?.phone || ''}</div>
                          </td>
                          <td style={{ padding: '12px 12px', fontSize: '0.72rem', color: T.sub, borderBottom: `1px solid ${T.border}` }}>{itemCount} article{itemCount !== 1 ? 's' : ''}</td>
                          <td style={{ padding: '12px 12px', fontFamily: "'DM Mono', monospace", fontWeight: 700, color: T.purple, borderBottom: `1px solid ${T.border}`, fontSize: '0.78rem' }}>{fmt(o.total_price || o.total)}</td>
                          <td style={{ padding: '12px 12px', fontFamily: "'DM Mono', monospace", fontSize: '0.68rem', color: T.sub, borderBottom: `1px solid ${T.border}` }}>{date}</td>
                          <td style={{ padding: '12px 12px', fontSize: '0.72rem', color: T.sub, borderBottom: `1px solid ${T.border}` }}>{o.payment_method || 'Carte'}</td>
                          <td style={{ padding: '12px 12px', borderBottom: `1px solid ${T.border}` }}><StatusBadge status={o.status} /></td>
                          <td style={{ padding: '12px 12px', borderBottom: `1px solid ${T.border}`, textAlign: 'right' }}>{icons.more}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Pagination */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '12px 20px', borderTop: `1px solid ${T.border}` }}>
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.62rem', color: T.sub, flex: 1 }}>
                    Page {orderPage + 1} sur {totalPages} · {orders.length} commandes
                  </span>
                  <button onClick={() => setOrderPage(p => Math.max(0, p - 1))} disabled={orderPage === 0}
                    style={{ width: 26, height: 26, borderRadius: 6, background: T.s2, border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', opacity: orderPage === 0 ? 0.4 : 1 }}>
                    {icons.chevL}
                  </button>
                  <button onClick={() => setOrderPage(p => Math.min(totalPages - 1, p + 1))} disabled={orderPage >= totalPages - 1}
                    style={{ width: 26, height: 26, borderRadius: 6, background: T.s2, border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', opacity: orderPage >= totalPages - 1 ? 0.4 : 1 }}>
                    {icons.chevR}
                  </button>
                </div>
              </div>
            </>
          )}
          </>}
        </div>
      </div>
    </div>
  );
}
